import React, { useState, useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import "./SearchForm.css";
import AutoSuggest from "./AutoSuggest";
import DatePickerComponent from "./DatePickerComponent";
import RoomSelectionComponent from "./RoomSelectionComponent";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import moment from "moment";
import { useDispatch } from "react-redux";
import { hotelSearch } from "../../../../redux/services/operations/hotel";
import { toast } from "react-toastify";
import axios from "axios";
import RecentHotelSearch from "../../../HotelsNew/HotelDesktopView/RecentSearch";

// Popular cities for autosuggestion
const popularCities = [
  {
    id: 130443,
    CityName: "New Delhi",
    CountryName: "India",
    CountryCode: "IN",
    isTopCity: true,
    category: "domestic",
  },
  {
    id: 144306,
    CityName: "Mumbai",
    CountryName: "India",
    CountryCode: "IN",
    isTopCity: true,
    category: "domestic",
  },
  {
    id: 111124,
    CityName: "Bangalore",
    CountryName: "India",
    CountryCode: "IN",
    isTopCity: true,
    category: "domestic",
  },
  {
    id: 133133,
    CityName: "Pune",
    CountryName: "India",
    CountryCode: "IN",
    isTopCity: true,
    category: "domestic",
  },
  {
    id: 100263,
    CityName: "Ahmedabad",
    CountryName: "India",
    CountryCode: "IN",
    isTopCity: true,
    category: "domestic",
  },
  {
    id: 122175,
    CityName: "Jaipur",
    CountryName: "India",
    CountryCode: "IN",
    isTopCity: true,
    category: "domestic",
  },
  {
    id: 100589,
    CityName: "Agra",
    CountryName: "India",
    CountryCode: "IN",
    isTopCity: true,
    category: "domestic",
  },
  {
    id: 145710,
    CityName: "Hyderabad",
    CountryName: "India",
    CountryCode: "IN",
    isTopCity: true,
    category: "domestic",
  },
  {
    id: 115936,
    CityName: "Dubai",
    CountryName: "United Arab Emirates",
    CountryCode: "AE",
    isTopCity: true,
    category: "international",
  },
  {
    id: 100765,
    CityName: "Abu Dhabi",
    CountryName: "United Arab Emirates",
    CountryCode: "AE",
    isTopCity: true,
    category: "international",
  },
  {
    id: 138703,
    CityName: "Singapore",
    CountryName: "Singapore",
    CountryCode: "SG",
    isTopCity: true,
    category: "international",
  },
  {
    id: 144092,
    CityName: "Bangkok",
    CountryName: "Thailand",
    CountryCode: "TH",
    isTopCity: true,
    category: "international",
  },
];

const HOTEL_CACHE_NAME = "hotel-search-cache";
const HOTEL_CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 1 month

export const insertHotelSearchIntoCache = async (hotelData) => {
  const cache = await caches.open(HOTEL_CACHE_NAME);

  const timestampedData = { ...hotelData, timestamp: Date.now() };

  const response = new Response(JSON.stringify(timestampedData), {
    headers: { "Content-Type": "application/json" },
  });

  // Unique key: City-startDate-endDate-timestamp
  const cacheKey = `${hotelData.city}-${hotelData.startDate}-${hotelData.endDate}-${timestampedData.timestamp}`;

  await cache.put(new Request(cacheKey), response);

  // Enforce max 8 entries
  const keys = await cache.keys();
  if (keys.length > 8) {
    const keyDataPairs = await Promise.all(
      keys.map(async (key) => {
        const res = await cache.match(key);
        const data = await res.json();
        return { key, timestamp: data.timestamp };
      })
    );

    keyDataPairs.sort((a, b) => a.timestamp - b.timestamp);

    const keysToDelete = keyDataPairs.slice(0, keys.length - 8);
    for (let { key } of keysToDelete) {
      await cache.delete(key);
    }
  }
};

export const fetchValidHotelSearchesFromCache = async () => {
  const cache = await caches.open(HOTEL_CACHE_NAME);
  const keys = await cache.keys();

  const validSearches = [];

  for (let key of keys) {
    const res = await cache.match(key);
    const data = await res.json();

    if (Date.now() - data.timestamp < HOTEL_CACHE_DURATION) {
      validSearches.push(data);
    } else {
      await cache.delete(key);
    }
  }

  return validSearches.sort((a, b) => b.timestamp - a.timestamp).slice(0, 8);
};

const SearchFormMobile = ({
  onSearchComplete,
  initialCityName,
  initialCityId,
  initialStartDate,
  initialEndDate,
  initialRooms,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isCityInputSelected, setCityInputSelected] = useState(false);
  const [isRoomInputSelected, setRoomInputSelected] = useState(false);
  const [selectedCity, setSelectedCity] = useState("New Delhi");
  const [isCalenderSelected, setCalenderSelected] = useState(false);
  const [selectedCityId, setSelectedCityId] = useState(130443);
  const [rooms, setRooms] = useState([
    { adults: 2, children: 0, childrenAges: [] },
  ]);
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment().add(1, "day"));
  const [focusedInput, setFocusedInput] = useState(null);

  const [active2, setActive2] = useState(false);
  const [active3, setActive3] = useState(false);
  const [active, setActive] = useState(true);

  // Static hotel cities data states
  const [staticHotelCities, setStaticHotelCities] = useState([]);
  const [cities2, setCities2] = useState(popularCities);
  const [isLoadingFrom, setIsLoadingFrom] = useState(false);

  const [searchParams] = useSearchParams();

  // Load static hotel cities data from HotelCities.js file
  useEffect(() => {
    const loadStaticHotelCities = async () => {
      try {
        const response = await fetch(
          `${process.env.PUBLIC_URL || ""}/HotelCities.js`,
          { cache: "no-store" }
        );
        const scriptText = await response.text();

        // Transform the script to extract the hotelCities array
        const transformed = scriptText
          .replace(/^[\s\S]*?export\s+const\s+hotelCities\s*=\s*/m, "return ")
          .replace(/;\s*$/, ";");
        const parser = new Function(transformed);
        const cities = parser();

        if (Array.isArray(cities)) {
          setStaticHotelCities(cities);
        }
      } catch (e) {
        console.error("Error loading static hotel cities data:", e);
      }
    };
    loadStaticHotelCities();
  }, []);

  // Filter and rank cities based on search value
  const filterAndRankCities = (cities, value) => {
    const v = (value || "").toLowerCase();
    if (!v) return [];
    return cities
      .filter((city) => {
        return (
          city &&
          ((city.CityName && String(city.CityName).toLowerCase().includes(v)) ||
            (city.CountryName &&
              String(city.CountryName).toLowerCase().includes(v)) ||
            (city.StateProvince &&
              String(city.StateProvince).toLowerCase().includes(v)) ||
            (city.DestinationId &&
              String(city.DestinationId).toLowerCase().includes(v)))
        );
      })
      .map((city) => {
        let priority = 3;
        if (city.CityName && String(city.CityName).toLowerCase().includes(v)) {
          priority = 1;
        } else if (
          city.CountryName &&
          String(city.CountryName).toLowerCase().includes(v)
        ) {
          priority = 2;
        } else if (
          city.StateProvince &&
          String(city.StateProvince).toLowerCase().includes(v)
        ) {
          priority = 3;
        }
        return { ...city, priority };
      })
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 20); // Limit to 20 results
  };

  const mergeResults = (staticResults, apiResults) => {
    const out = [];
    const seen = new Set();
    const keyFor = (item) => {
      const id = String(item.id || item.DestinationId || "").trim();
      if (id) return `id:${id}`;
      const cn = String(item.CityName || "")
        .trim()
        .toLowerCase();
      const co = String(item.CountryName || "")
        .trim()
        .toLowerCase();
      return `name:${cn}|${co}`;
    };
    for (const s of staticResults) {
      const k = keyFor(s);
      if (!seen.has(k)) {
        seen.add(k);
        out.push(s);
      }
    }
    for (const a of apiResults) {
      const k = keyFor(a);
      if (!seen.has(k)) {
        seen.add(k);
        out.push(a);
      }
    }
    return out.slice(0, 20);
  };

  useEffect(() => {
    const from = searchParams.get("from") || "";
    const cityNameParam = searchParams.get("cityName") || "";
    const start = searchParams.get("startDate");
    const end = searchParams.get("endDate");
    const roomsParam = searchParams.get("rooms");

    if (cityNameParam) setSelectedCity(cityNameParam);
    if (from) setSelectedCityId(from);
    if (start) setStartDate(moment(start));
    if (end) setEndDate(moment(end));
    if (roomsParam) {
      try {
        const parsedRooms = JSON.parse(roomsParam);
        setRooms(parsedRooms);
      } catch (err) {
        console.warn("Invalid rooms JSON", err);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (initialCityName) setSelectedCity(initialCityName);
    if (initialCityId) setSelectedCityId(initialCityId);
    if (initialStartDate) setStartDate(moment(initialStartDate));
    if (initialEndDate) setEndDate(moment(initialEndDate));
    if (Array.isArray(initialRooms) && initialRooms.length)
      setRooms(initialRooms);
    if (initialEndDate) {
      setActive2(true);
      setActive(false);
    }
  }, [
    initialCityName,
    initialCityId,
    initialStartDate,
    initialEndDate,
    initialRooms,
  ]);

  const updateQueryParam = (key, value) => {
    const params = new URLSearchParams(location.search);
    if (value !== undefined && value !== null) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city.CityName);
    setSelectedCityId(city.id || city.DestinationId);
    setCityInputSelected(false);
    updateQueryParam("cityName", city.CityName);
    updateQueryParam("from", city.id || city.DestinationId);
  };

  const handleCityInputFocus = () => {
    setCityInputSelected(true);
    // Show popular cities when input is focused
    setCities2(popularCities);
    setIsLoadingFrom(false);
  };
  const handleRoomInputFocus = () => setRoomInputSelected(true);
  const closeRoomInput = () => setRoomInputSelected(false);
  const closeCityInput = () => setCityInputSelected(false);

  const fetchDatas = async (value) => {
    try {
      const requestData = { city: value };
      const response = await axios.post(
        "https://admin.travelsdata.com/api/Hotel/CityList",
        requestData
      );
      const json = response.data;

      const results = json.data
        .filter((user) => {
          return (
            user &&
            ((user.CityName &&
              user.CityName.toLowerCase().includes(value.toLowerCase())) ||
              (user.CountryName &&
                user.CountryName.toLowerCase().includes(value.toLowerCase())))
          );
        })
        .map((user) => {
          let priority = 3;
          if (
            user.CityName &&
            user.CityName.toLowerCase().includes(value.toLowerCase())
          ) {
            priority = 1;
          } else if (
            user.CountryName &&
            user.CountryName.toLowerCase().includes(value.toLowerCase())
          ) {
            priority = 2;
          }
          return { ...user, priority };
        })
        .sort((a, b) => a.priority - b.priority)
        .slice(0, 20);

      return results;
    } catch (error) {
      console.error("Error fetching data:", error);
      return [];
    }
  };

  const latestQueryRef = useRef("");

  const handleCitySearch = async (value) => {
    const typed = value || "";
    const lower = typed.trim().toLowerCase();

    if (lower.length === 0) {
      // Show popular cities when input is empty
      setCities2(popularCities);
      setIsLoadingFrom(false);
      return;
    }

    if (lower.length < 3) {
      setCities2(popularCities);
      setIsLoadingFrom(false);
      return;
    }

    latestQueryRef.current = lower;
    const staticMatches = filterAndRankCities(staticHotelCities, lower);
    const formattedMatches = staticMatches.map((city) => ({
      id: parseInt(city.DestinationId),
      CityName: city.CityName.split(",")[0].trim(),
      CountryName: city.CountryName,
      CountryCode: city.CountryCode,
      StateProvince: city.StateProvince,
      isStaticData: true,
    }));
    if (formattedMatches.length > 0) {
      setCities2(formattedMatches);
    } else {
      setCities2([]);
    }
    setIsLoadingFrom(true);
    const apiResults = await fetchDatas(lower);
    if (latestQueryRef.current !== lower) return;
    const merged =
      formattedMatches.length > 0
        ? mergeResults(formattedMatches, apiResults)
        : apiResults;
    setCities2(merged);
    setIsLoadingFrom(false);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleCalenderFocus = (input) => {
    setCalenderSelected(true);
    if (!isCalenderSelected) {
      if (input === "startDate") setFocusedInput("startDate");
      else if (input === "endDate") setFocusedInput("endDate");
      else {
        setCalenderSelected(!isCalenderSelected);
        if (!isCalenderSelected) setFocusedInput("startDate");
        else setFocusedInput(null);
      }
    }
  };

  const closeDateInput = () => setCalenderSelected(false);

  const syncRoomsToQuery = (updatedRooms) => {
    // updateQueryParam("rooms", JSON.stringify(updatedRooms));
  };

  const addRoom = () => {
    if (rooms.length < 6) {
      const updated = [...rooms, { adults: 2, children: 0, childrenAges: [] }];
      setRooms(updated);
      syncRoomsToQuery(updated);
    }
  };

  const removeRoom = (index) => {
    if (rooms.length > 1) {
      const updated = [...rooms];
      updated.splice(index, 1);
      setRooms(updated);
      syncRoomsToQuery(updated);
    }
  };

  const updateRoom = (index, field, value) => {
    const updatedRooms = [...rooms];
    updatedRooms[index][field] = value;
    if (field === "children") {
      updatedRooms[index].childrenAges = Array(value).fill(null);
    }
    setRooms(updatedRooms);
    syncRoomsToQuery(updatedRooms);
  };

  const updateChildAge = (roomIndex, childIndex, age) => {
    const updatedRooms = [...rooms];
    updatedRooms[roomIndex].childrenAges[childIndex] = age;
    setRooms(updatedRooms);
    syncRoomsToQuery(updatedRooms);
  };

  const handleSearchFlight = () => {
    setActive(true);
    setActive2(false);
    setActive3(false);
    setEndDate(null);
  };

  const handleSearchFlightRound = () => {
    setActive2(true);
    setActive(false);
    setActive3(false);
  };

  const handleSearchFlightMultiPle = () => {
    setActive3(true);
    setActive(false);
    setActive2(false);
  };

  const [cacheData, setCacheData] = useState("");
  useEffect(() => {
    const data = async () => {
      const cachedSearches = await fetchValidHotelSearchesFromCache();
      if (cachedSearches) {
        setCacheData(cachedSearches);
      }
    };
    data();
  }, []);
  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   if (!startDate || !endDate) {
  //     toast.error(`Please fill out all fields ${startDate} ${endDate}`);
  //     return;
  //   }

  //   // const formDataToPass = {
  //   //   from: selectedCityId,
  //   //   startDate: startDate.format("YYYY-MM-DD"),
  //   //   endDate: endDate.format("YYYY-MM-DD"),
  //   //   rooms: JSON.stringify(rooms),
  //   // };
  //   const formDataToPass = {
  //     from: selectedCityId,
  //     city: selectedCity,
  //     startDate: startDate.format("YYYY-MM-DD"),
  //     endDate: endDate.format("YYYY-MM-DD"),
  //     rooms: JSON.stringify(rooms),
  //   };

  //   const queryString = new URLSearchParams(formDataToPass).toString();
  //   navigate(`/hotelmodify?${queryString}`);

  //   if (onSearchComplete) {
  //     onSearchComplete();
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!startDate || !endDate) {
      toast.error("Please fill out all fields");
      return;
    }

    const formDataToPass = {
      from: selectedCityId,
      city: selectedCity,
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
      rooms: JSON.stringify(rooms),
    };
    localStorage.setItem("hotelRoomsConfig", JSON.stringify(rooms));
    localStorage.setItem("hotelCin", startDate.format("YYYY-MM-DD"));
    localStorage.setItem("hotelCout", endDate.format("YYYY-MM-DD"));
    // ✅ Build URL (same as Recent Search click)
    const queryString = new URLSearchParams(formDataToPass).toString();
    navigate(`/hotelmodify?${queryString}`);

    // ⭐ SAVE TO CACHE FOR RECENT SEARCHES
    await insertHotelSearchIntoCache({
      from: selectedCityId,
      city: selectedCity,
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
      rooms: rooms, // store actual array, not string
    });

    if (onSearchComplete) {
      onSearchComplete();
    }
  };

  return (
    <div className="container_tbs hotelsearchmobile">
      <div
        className="tab-content-fs hotel_tab_search_form"
        style={{ marginTop: "20px" }}
      >
        <div className="pd20_n">
          <h1 className="app-hide mb_title">
            Same hotel, Cheapest price. Guaranteed!
          </h1>
          <div id="divOneWayRT">
            <div className="wid46 fl m_rel" style={{ width: "100%" }}>
              <Form
                method="post"
                action="#"
                autoComplete="off"
                onSubmit={handleSubmit}
                className="ng-pristine ng-valid"
              >
                <div className="lb_bg mgt13 hgt93">
                  <p className="_ft-sz f13">
                    Enter City name, Location or Specific hotel
                  </p>
                  <div className="fs_srch">
                    {/* <input
                      id="txtCityForMobile"
                      type="text"
                      autoComplete="off"
                      className="hl-input"
                      placeholder={selectedCity ? selectedCity : "Enter City/Hotel Name"}
                      onFocus={handleCityInputFocus}
                    /> */}
                    <input
                      id="txtCityForMobile"
                      type="text"
                      readOnly
                      autoComplete="off"
                      className="hl-input"
                      placeholder="Enter City/Hotel Name"
                      value={selectedCity}
                      onFocus={handleCityInputFocus}
                    />
                  </div>
                </div>
              </Form>
            </div>

            <div
              controlId="ddate"
              className="wid46 fl mgt13"
              onClick={handleCalenderFocus}
            >
              <div className="lb_bg ddateClass" id="dvfarecal">
                <p className="inpttl2">Check-In</p>
                <input
                  type="text"
                  readOnly
                  className="hl-input2"
                  value={startDate ? startDate.format("MM/DD/YYYY") : ""}
                  onClick={() => handleCalenderFocus("startDate")}
                />
              </div>
            </div>

            <div controlId="rdate" className="wid46 fr mgt13">
              <div className="retu-date-n" id="rdatelbl">
                <div className="lb_bg m_rel">
                  <div className="rdateClass" id="rdateFade">
                    <p className="inpttl2">Check-Out</p>
                    <input
                      type="text"
                      readOnly
                      className="hl-input2 cl-bl hide-ddate round-but1"
                      value={endDate ? endDate.format("MM/DD/YYYY") : ""}
                      onClick={() => {
                        handleCalenderFocus("endDate");
                        handleSearchFlightRound();
                      }}
                    />
                    <input type="hidden" name="hdn1" id="hdn1" />
                    <input id="hdn" type="hidden" />
                  </div>
                  <i
                    className="cross_i crs2"
                    id="divCross"
                    onClick={handleSearchFlight}
                  />
                </div>
              </div>
            </div>

            <div className="noOfAdl mgt-10">
              <div
                controlId="travellers"
                className="wid46 fl mgt13"
                style={{ width: "100%" }}
              >
                <div className="_en-icon"></div>
                <div className="lb_bg ad-ch-sec" onClick={handleRoomInputFocus}>
                  <p className="_ft-sz f13 mgb3">Guest and Room</p>
                  <span className="ttl_ad">
                    <span style={{ fontSize: "18px" }}>
                      {rooms.reduce(
                        (total, room) => total + room.adults + room.children,
                        0
                      )}
                    </span>{" "}
                    Guest,
                    <span style={{ fontSize: "18px" }}>
                      {rooms.length}
                    </span>{" "}
                    {rooms.length === 1 ? "Room" : "Rooms"}
                  </span>
                  <span className="circle plus"></span>
                </div>
              </div>
            </div>

            <div className="clr" />
            <input
              name=""
              id="btnSearch"
              value="Search"
              onClick={handleSubmit}
              style={{ WebkitAppearance: "none" }}
              type="button"
              className="fs_btn"
            />
          </div>
        </div>
      </div>
      <div style={{ paddingLeft: "10px", color: "#333" }}>
        {location.pathname === "/" &&  <RecentHotelSearch cacheData={cacheData} />}
        
      </div>
      <div className="clr"></div>
      {isCityInputSelected && (
        <AutoSuggest
          closeCityInput={closeCityInput}
          handleCitySelect={handleCitySelect}
          cities2={cities2}
          setCities2={setCities2}
          handleCitySearch={handleCitySearch}
          loading={isLoadingFrom}
        />
      )}
      {isRoomInputSelected && (
        <RoomSelectionComponent
          rooms={rooms}
          addRoom={addRoom}
          removeRoom={removeRoom}
          updateRoom={updateRoom}
          closeRoomInput={closeRoomInput}
          updateChildAge={updateChildAge}
        />
      )}
      {isCalenderSelected && (
        <DatePickerComponent
          closeDateInput={closeDateInput}
          startDate={startDate}
          endDate={endDate}
          setStartDate={handleStartDateChange}
          setEndDate={handleEndDateChange}
          focusedInput={focusedInput}
          setFocusedInput={setFocusedInput}
        />
      )}
    </div>
  );
};

export default SearchFormMobile;
